import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { 
  Star, 
  TrendingUp, 
  Users, 
  MessageSquare, 
  Search, 
  Filter, 
  Calendar, 
  Award, 
  Eye, 
  CheckCircle2, 
  ChevronLeft, 
  ChevronRight,
  RotateCcw,
  Loader2,
  Building2,
  UserCheck,
  PieChart as PieChartIcon,
  Layers,
  Sparkles,
  Download
} from 'lucide-react';
import { Modal } from 'antd';
import CustomSelect from '../base/CustomSelect';
import { 
  PieChart, 
  Pie, 
  Cell, 
  Tooltip, 
  Legend, 
  ResponsiveContainer 
} from 'recharts';
import apiClient from '../../utils/apiClient';
import { STATISTICAL_REPORT_API } from '../../apis/statisticalReport';
import { showToast } from '../../utils/toastNotification';

const stars = [5, 4, 3, 2, 1];

const SCORE_COLORS = {
  5: '#10B981', // Emerald
  4: '#06B6D4', // Cyan
  3: '#F59E0B', // Amber
  2: '#F97316', // Orange
  1: '#EF4444', // Red
};

const SOURCE_COLORS = {
  COUNTER: '#3B82F6', // Blue
  LEADER: '#8B5CF6',  // Purple
  PHAN_ANH: '#F59E0B', // Amber
};

const scoreBg = (score) => {
  const s = Number(score) || 0;
  if (s <= 2) return 'bg-rose-50 text-rose-700 border-rose-200';
  if (s === 3) return 'bg-amber-50 text-amber-700 border-amber-200';
  return 'bg-emerald-50 text-emerald-700 border-emerald-200';
};

const COUNTER_NAME_MAP = {
  QUAY_1: 'Quầy 1',
  QUAY_2: 'Quầy 2',
  QUAY_3: 'Quầy 3',
  QUAY_4: 'Quầy 4',
  QUAY_5: 'Quầy 5',
  QUAY_6: 'Quầy 6',
  QUAY_7: 'Quầy 7',
  QUAY_8: 'Quầy 8',
};

const getCounterLabel = (code) => {
  if (!code) return 'Quầy tiếp dân';
  if (COUNTER_NAME_MAP[code]) return COUNTER_NAME_MAP[code];
  const match = /QUAY_(\d+)/i.exec(code);
  if (match) return `Quầy ${match[1]}`;
  return code;
};

export default function FeedbackTab({ dateRange }) {
  // Source Filter: 'ALL' | 'COUNTER' | 'LEADER' | 'PHAN_ANH'
  const [sourceType, setSourceType] = useState('ALL');

  // Loading states
  const [loading, setLoading] = useState(false);
  const [statsLoading, setStatsLoading] = useState(false);

  // Counter ratings & stats
  const [counterStats, setCounterStats] = useState({
    totalRatings: 0,
    averageScore: 0,
    satisfactionRate: 0,
    scoreDistribution: []
  });
  const [counterRatings, setCounterRatings] = useState([]);

  // Leader meeting ratings & stats
  const [leaderStats, setLeaderStats] = useState({
    totalRatings: 0,
    averageScore: 0,
    satisfactionRate: 0,
    scoreDistribution: []
  });
  const [leaderRatings, setLeaderRatings] = useState([]);

  // Phan anh ratings & stats
  const [phanAnhStats, setPhanAnhStats] = useState({
    totalRatings: 0,
    averageScore: 0,
    satisfactionRate: 0,
    scoreDistribution: []
  });
  const [phanAnhRatings, setPhanAnhRatings] = useState([]);

  // Filter states
  const [search, setSearch] = useState('');
  const [scoreFilter, setScoreFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  // Detail Modal state
  const [selectedRating, setSelectedRating] = useState(null);

  // Excel Export state & handler
  const [exportLoading, setExportLoading] = useState(false);

  const handleExportExcel = async () => {
    try {
      setExportLoading(true);
      const params = {};
      if (dateRange?.from) params.from = dateRange.from;
      if (dateRange?.to) params.to = dateRange.to;

      const blob = await STATISTICAL_REPORT_API.exportDanhGiaReport(params);
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'danh_sach_danh_gia.xlsx');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      showToast.success('Xuất danh sách đánh giá thành công');
    } catch (err) {
      console.error('Export feedback ratings failed', err);
      showToast.error(err.message || 'Xuất Excel thất bại. Vui lòng kiểm tra quyền hạn.');
    } finally {
      setExportLoading(false);
    }
  };

  // 1. Fetch Statistics from all 3 APIs in parallel
  const fetchAllStats = useCallback(async () => {
    try {
      setStatsLoading(true);
      const params = {};
      if (dateRange?.from) params.fromDate = dateRange.from;
      if (dateRange?.to) params.toDate = dateRange.to;

      const [counterRes, leaderRes, phanAnhRes] = await Promise.allSettled([
        apiClient.get('/api/reception-ratings/statistics', { params }),
        apiClient.get('/api/leader-meeting-ratings/statistics', { params }),
        apiClient.get('/api/phan-anh-ratings/statistics', { params })
      ]);

      if (counterRes.status === 'fulfilled') {
        const d = counterRes.value.data?.data || counterRes.value.data || {};
        setCounterStats({
          totalRatings: d.totalRatings || d.totalFeedbacks || 0,
          averageScore: Number(d.averageScore || d.avgRating || 0),
          satisfactionRate: d.satisfactionRate ? Math.round(d.satisfactionRate) : 0,
          scoreDistribution: Array.isArray(d.scoreDistribution) ? d.scoreDistribution : []
        });
      }

      if (leaderRes.status === 'fulfilled') {
        const d = leaderRes.value.data?.data || leaderRes.value.data || {};
        setLeaderStats({
          totalRatings: d.totalRatings || 0,
          averageScore: Number(d.averageScore || 0),
          satisfactionRate: d.satisfactionRate ? Math.round(d.satisfactionRate) : 0,
          scoreDistribution: Array.isArray(d.scoreDistribution) ? d.scoreDistribution : []
        });
      }

      if (phanAnhRes.status === 'fulfilled') {
        const d = phanAnhRes.value.data?.data || phanAnhRes.value.data || {};
        setPhanAnhStats({
          totalRatings: d.totalRatings || 0,
          averageScore: Number(d.averageScore || 0),
          satisfactionRate: d.satisfactionRate ? Math.round(d.satisfactionRate) : 0,
          scoreDistribution: Array.isArray(d.scoreDistribution) ? d.scoreDistribution : []
        });
      }
    } catch (err) {
      console.error("Failed to fetch feedback statistics", err);
    } finally {
      setStatsLoading(false);
    }
  }, [dateRange?.from, dateRange?.to]);

  // 2. Fetch Ratings List from all 3 APIs
  const fetchAllRatings = useCallback(async () => {
    try {
      setLoading(true);
      const params = { page: 1, limit: 100, size: 100 };
      if (dateRange?.from) params.fromDate = dateRange.from;
      if (dateRange?.to) params.toDate = dateRange.to;

      const [counterRes, leaderRes, phanAnhRes] = await Promise.allSettled([
        apiClient.get('/api/reception-ratings', { params }),
        apiClient.get('/api/leader-meeting-ratings', { params }),
        apiClient.get('/api/phan-anh-ratings', { params })
      ]);

      if (counterRes.status === 'fulfilled') {
        const raw = counterRes.value.data?.data || counterRes.value.data;
        const list = Array.isArray(raw) 
          ? raw 
          : (Array.isArray(raw?.data) ? raw.data : (Array.isArray(raw?.items) ? raw.items : []));
        setCounterRatings(list.map(item => ({
          ...item,
          sourceType: 'COUNTER',
          sourceLabel: 'Tiếp dân tại quầy',
          targetName: item.officerName || 'Cán bộ trực quầy',
          placeLabel: getCounterLabel(item.counterCode || item.department),
          receptionCode: item.receptionCode || item.ticketNo || item.id,
          citizenName: item.citizenName || item.applicantName || 'Công dân',
          score: Number(item.score) || 5,
          comment: item.comment || '',
          ratedAt: item.ratedAt || item.createdAt
        })));
      }

      if (leaderRes.status === 'fulfilled') {
        const raw = leaderRes.value.data?.data || leaderRes.value.data;
        const list = Array.isArray(raw) 
          ? raw 
          : (Array.isArray(raw?.data) ? raw.data : (Array.isArray(raw?.items) ? raw.items : []));
        setLeaderRatings(list.map(item => ({
          ...item,
          sourceType: 'LEADER',
          sourceLabel: 'Gặp lãnh đạo',
          targetName: item.leader?.fullName || item.leaderName || 'Lãnh đạo UBND',
          placeLabel: item.location || 'Phòng tiếp công dân',
          receptionCode: item.registrationCode || item.receptionCode || item.id,
          citizenName: item.applicantName || item.citizenName || 'Công dân',
          score: Number(item.score) || 5,
          comment: item.comment || '',
          workingContent: item.topic || item.workingContent || '',
          ratedAt: item.ratedAt || item.createdAt
        })));
      }

      if (phanAnhRes.status === 'fulfilled') {
        const raw = phanAnhRes.value.data?.data || phanAnhRes.value.data;
        const list = Array.isArray(raw) 
          ? raw 
          : (Array.isArray(raw?.data) ? raw.data : (Array.isArray(raw?.items) ? raw.items : []));
        setPhanAnhRatings(list.map(item => ({
          ...item,
          sourceType: 'PHAN_ANH',
          sourceLabel: 'Phản ánh kiến nghị',
          targetName: item.complaint?.category?.name || 'Lĩnh vực phản ánh',
          placeLabel: item.complaint?.title ? `Tiêu đề: ${item.complaint.title}` : 'Phản ánh trực tuyến',
          receptionCode: item.complaint?.code || item.id,
          citizenName: item.citizenName || 'Người dân',
          score: Number(item.score) || 5,
          comment: item.comment || '',
          workingContent: item.complaint?.title || '',
          categoryName: item.complaint?.category?.name || '',
          ratedAt: item.ratedAt || item.createdAt
        })));
      }
    } catch (err) {
      console.error("Failed to fetch all ratings list", err);
    } finally {
      setLoading(false);
    }
  }, [dateRange?.from, dateRange?.to]);

  useEffect(() => {
    fetchAllStats();
    fetchAllRatings();
  }, [fetchAllStats, fetchAllRatings]);

  // 3. Combined Stats Calculation based on current sourceType
  const currentStats = useMemo(() => {
    if (sourceType === 'COUNTER') {
      return {
        totalRatings: counterStats.totalRatings,
        averageScore: counterStats.averageScore ? counterStats.averageScore.toFixed(1) : '5.0',
        satisfactionRate: counterStats.satisfactionRate,
        scoreDist: counterStats.scoreDistribution
      };
    }

    if (sourceType === 'LEADER') {
      return {
        totalRatings: leaderStats.totalRatings,
        averageScore: leaderStats.averageScore ? leaderStats.averageScore.toFixed(1) : '5.0',
        satisfactionRate: leaderStats.satisfactionRate,
        scoreDist: leaderStats.scoreDistribution
      };
    }

    if (sourceType === 'PHAN_ANH') {
      return {
        totalRatings: phanAnhStats.totalRatings,
        averageScore: phanAnhStats.averageScore ? phanAnhStats.averageScore.toFixed(1) : '5.0',
        satisfactionRate: phanAnhStats.satisfactionRate,
        scoreDist: phanAnhStats.scoreDistribution
      };
    }

    // ALL (Combined)
    const total = counterStats.totalRatings + leaderStats.totalRatings + phanAnhStats.totalRatings;
    const weightedAvg = total > 0 
      ? ((counterStats.averageScore * counterStats.totalRatings + leaderStats.averageScore * leaderStats.totalRatings + phanAnhStats.averageScore * phanAnhStats.totalRatings) / total).toFixed(1)
      : '5.0';

    const weightedSatisfaction = total > 0
      ? Math.round((counterStats.satisfactionRate * counterStats.totalRatings + leaderStats.satisfactionRate * leaderStats.totalRatings + phanAnhStats.satisfactionRate * phanAnhStats.totalRatings) / total)
      : 100;

    const mergedDist = [1, 2, 3, 4, 5].map(s => {
      const cCount = counterStats.scoreDistribution.find(d => (d.score || d.star) === s)?.count || 0;
      const lCount = leaderStats.scoreDistribution.find(d => (d.score || d.star) === s)?.count || 0;
      const pCount = phanAnhStats.scoreDistribution.find(d => (d.score || d.star) === s)?.count || 0;
      return { score: s, count: cCount + lCount + pCount };
    });

    return {
      totalRatings: total,
      averageScore: weightedAvg,
      satisfactionRate: weightedSatisfaction,
      scoreDist: mergedDist
    };
  }, [sourceType, counterStats, leaderStats, phanAnhStats]);

  // 4. Data for Pie Charts
  const scorePieData = useMemo(() => {
    const dist = currentStats.scoreDist || [];
    return [5, 4, 3, 2, 1].map(star => {
      const item = dist.find(d => (d.score || d.star) === star);
      const count = item?.count || item?.total || 0;
      return {
        name: `${star} Sao`,
        star,
        value: count,
        color: SCORE_COLORS[star]
      };
    }).filter(item => currentStats.totalRatings === 0 || item.value > 0);
  }, [currentStats]);

  const sourcePieData = useMemo(() => {
    return [
      {
        name: 'Quầy tiếp dân',
        value: counterStats.totalRatings,
        color: SOURCE_COLORS.COUNTER
      },
      {
        name: 'Gặp lãnh đạo',
        value: leaderStats.totalRatings,
        color: SOURCE_COLORS.LEADER
      },
      {
        name: 'Phản ánh kiến nghị',
        value: phanAnhStats.totalRatings,
        color: SOURCE_COLORS.PHAN_ANH
      }
    ].filter(item => item.value > 0);
  }, [counterStats.totalRatings, leaderStats.totalRatings, phanAnhStats.totalRatings]);

  // 5. Filtered & Sorted Ratings List
  const filteredRatings = useMemo(() => {
    let list = [];
    if (sourceType === 'ALL') {
      list = [...counterRatings, ...leaderRatings, ...phanAnhRatings];
    } else if (sourceType === 'COUNTER') {
      list = [...counterRatings];
    } else if (sourceType === 'LEADER') {
      list = [...leaderRatings];
    } else {
      list = [...phanAnhRatings];
    }

    // Filter by search
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter(item => 
        (item.receptionCode || '').toLowerCase().includes(q) ||
        (item.citizenName || '').toLowerCase().includes(q) ||
        (item.targetName || '').toLowerCase().includes(q) ||
        (item.placeLabel || '').toLowerCase().includes(q) ||
        (item.comment || '').toLowerCase().includes(q) ||
        (item.workingContent || '').toLowerCase().includes(q)
      );
    }

    // Filter by score
    if (scoreFilter) {
      list = list.filter(item => item.score === Number(scoreFilter));
    }

    // Sort by latest
    list.sort((a, b) => new Date(b.ratedAt || 0) - new Date(a.ratedAt || 0));

    return list;
  }, [sourceType, counterRatings, leaderRatings, phanAnhRatings, search, scoreFilter]);

  // Pagination
  const totalItems = filteredRatings.length;
  const totalPages = Math.ceil(totalItems / pageSize) || 1;
  const paginatedRatings = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredRatings.slice(start, start + pageSize);
  }, [filteredRatings, currentPage, pageSize]);

  // Render Stars
  const renderStars = (score) => {
    const s = Number(score) || 0;
    return (
      <div className="flex items-center gap-0.5">
        {[1, 2, 3, 4, 5].map((i) => (
          <Star
            key={i}
            size={13}
            className={i <= s ? 'text-amber-400 fill-amber-400' : 'text-gray-200 fill-gray-200'}
          />
        ))}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* 1. KPI SUMMARY METRIC CARDS */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Card 1: Điểm trung bình */}
        <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-2xs hover:border-amber-300 transition-all">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">Điểm đánh giá TB</span>
            <div className="w-8 h-8 rounded-lg bg-amber-50 text-amber-600 flex items-center justify-center">
              <Star size={18} fill="currentColor" />
            </div>
          </div>
          <div className="mt-2 flex items-baseline gap-2">
            <span className="text-2xl sm:text-3xl font-black text-gray-900">{currentStats.averageScore}</span>
            <span className="text-xs text-gray-500 font-medium">/ 5.0</span>
          </div>
          <div className="mt-2 flex items-center gap-1">
            {renderStars(currentStats.averageScore)}
            <span className="text-[11px] text-gray-500 ml-1">Mức độ chung</span>
          </div>
        </div>

        {/* Card 2: Tổng lượt đánh giá */}
        <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-2xs hover:border-blue-300 transition-all">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">Tổng lượt đánh giá</span>
            <div className="w-8 h-8 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center">
              <MessageSquare size={18} />
            </div>
          </div>
          <div className="mt-2 flex items-baseline gap-2">
            <span className="text-2xl sm:text-3xl font-black text-blue-600">{currentStats.totalRatings}</span>
            <span className="text-xs text-gray-500">phiếu</span>
          </div>
        </div>

        {/* Card 3: Tỷ lệ hài lòng */}
        <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-2xs hover:border-emerald-300 transition-all">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">Tỷ lệ hài lòng (4-5★)</span>
            <div className="w-8 h-8 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center">
              <TrendingUp size={18} />
            </div>
          </div>
          <div className="mt-2 flex items-baseline gap-2">
            <span className="text-2xl sm:text-3xl font-black text-emerald-600">{currentStats.satisfactionRate}%</span>
            <span className="text-xs text-emerald-700 font-semibold bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-200">Rất tốt</span>
          </div>
          <div className="mt-2 w-full bg-gray-100 rounded-full h-1.5 overflow-hidden">
            <div
              className="bg-emerald-500 h-1.5 rounded-full transition-all duration-700"
              style={{ width: `${Math.min(100, Math.max(0, currentStats.satisfactionRate))}%` }}
            />
          </div>
        </div>

        {/* Card 4: Đánh giá xuất sắc */}
        <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-2xs hover:border-purple-300 transition-all">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">Phiếu 5 sao xuất sắc</span>
            <div className="w-8 h-8 rounded-lg bg-purple-50 text-purple-600 flex items-center justify-center">
              <Award size={18} />
            </div>
          </div>
          <div className="mt-2 flex items-baseline gap-2">
            <span className="text-2xl sm:text-3xl font-black text-purple-600">
              {currentStats.scoreDist?.find(d => (d.score || d.star) === 5)?.count || 0}
            </span>
            <span className="text-xs text-gray-500">phiếu 5★</span>
          </div>
          <p className="mt-2 text-[11px] text-gray-500">Chiếm {currentStats.totalRatings > 0 ? Math.round(((currentStats.scoreDist?.find(d => (d.score || d.star) === 5)?.count || 0) / currentStats.totalRatings) * 100) : 0}% tổng lượt đánh giá</p>
        </div>
      </div>

      {/* 3. BIỂU ĐỒ TRÒN THỐNG KÊ (PIE / DONUT CHARTS) */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Biểu đồ 1: Phân bố mức độ hài lòng (1★ - 5★) */}
        <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-2xs">
          <div className="flex items-center justify-between mb-2">
            <div>
              <h3 className="text-sm font-bold text-gray-900 flex items-center gap-1.5">
                <PieChartIcon className="w-4 h-4 text-emerald-600" />
                Cơ cấu phân bố đánh giá theo số sao
              </h3>
              <p className="text-xs text-gray-500 mt-0.5">Tỷ lệ phiếu từ 1 sao đến 5 sao</p>
            </div>
          </div>

          <div className="h-64 flex items-center justify-center">
            {currentStats.totalRatings === 0 ? (
              <div className="text-center text-gray-400 text-xs">
                <PieChartIcon className="w-8 h-8 mx-auto mb-1 opacity-40" />
                Chưa có dữ liệu đánh giá
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={scorePieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={85}
                    paddingAngle={4}
                    dataKey="value"
                    labelLine={false}
                    label={({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
                      if (!percent || percent < 0.05) return null;
                      const RADIAN = Math.PI / 180;
                      const radius = innerRadius + (outerRadius - innerRadius) * 0.52;
                      const x = cx + radius * Math.cos(-midAngle * RADIAN);
                      const y = cy + radius * Math.sin(-midAngle * RADIAN);
                      return (
                        <text
                          x={x}
                          y={y}
                          fill="#ffffff"
                          textAnchor="middle"
                          dominantBaseline="central"
                          style={{ fontSize: '11px', fontWeight: '800', filter: 'drop-shadow(0px 1px 2px rgba(0,0,0,0.5))' }}
                        >
                          {`${Math.round(percent * 100)}%`}
                        </text>
                      );
                    }}
                  >
                    {scorePieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip 
                    formatter={(val, name) => [`${val} phiếu (${Math.round((val / currentStats.totalRatings) * 100)}%)`, name]}
                    contentStyle={{ borderRadius: '8px', fontSize: '12px', border: '1px solid #E2E8F0' }}
                  />
                  <Legend 
                    verticalAlign="bottom" 
                    iconType="circle"
                    formatter={(val) => <span className="text-xs font-medium text-gray-700">{val}</span>}
                  />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* Biểu đồ 2: Cơ cấu đánh giá theo nguồn (Tiếp dân quầy vs Lãnh đạo) */}
        <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-2xs">
          <div className="flex items-center justify-between mb-2">
            <div>
              <h3 className="text-sm font-bold text-gray-900 flex items-center gap-1.5">
                <Layers className="w-4 h-4 text-blue-600" />
                Tỷ trọng đánh giá theo nguồn tiếp nhận
              </h3>
              <p className="text-xs text-gray-500 mt-0.5">Quầy tiếp dân so với Gặp lãnh đạo</p>
            </div>
          </div>

          <div className="h-64 flex items-center justify-center">
            {(counterStats.totalRatings + leaderStats.totalRatings) === 0 ? (
              <div className="text-center text-gray-400 text-xs">
                <PieChartIcon className="w-8 h-8 mx-auto mb-1 opacity-40" />
                Chưa có dữ liệu đánh giá
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={sourcePieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={85}
                    paddingAngle={6}
                    dataKey="value"
                    labelLine={false}
                    label={({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
                      if (!percent || percent < 0.05) return null;
                      const RADIAN = Math.PI / 180;
                      const radius = innerRadius + (outerRadius - innerRadius) * 0.52;
                      const x = cx + radius * Math.cos(-midAngle * RADIAN);
                      const y = cy + radius * Math.sin(-midAngle * RADIAN);
                      return (
                        <text
                          x={x}
                          y={y}
                          fill="#ffffff"
                          textAnchor="middle"
                          dominantBaseline="central"
                          style={{ fontSize: '11px', fontWeight: '800', filter: 'drop-shadow(0px 1px 2px rgba(0,0,0,0.5))' }}
                        >
                          {`${Math.round(percent * 100)}%`}
                        </text>
                      );
                    }}
                  >
                    {sourcePieData.map((entry, index) => (
                      <Cell key={`source-cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip 
                    formatter={(val, name) => {
                      const total = counterStats.totalRatings + leaderStats.totalRatings;
                      return [`${val} phiếu (${total > 0 ? Math.round((val / total) * 100) : 0}%)`, name];
                    }}
                    contentStyle={{ borderRadius: '8px', fontSize: '12px', border: '1px solid #E2E8F0' }}
                  />
                  <Legend 
                    verticalAlign="bottom" 
                    iconType="circle"
                    formatter={(val) => <span className="text-xs font-medium text-gray-700">{val}</span>}
                  />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </div>

      {/* 4. FILTER & TABLE SECTION */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-2xs relative z-20">
        {/* Table Toolbar */}
        <div className="p-4 border-b border-gray-100 flex flex-col md:flex-row items-center justify-between gap-3 bg-gray-50/50 rounded-t-xl">
          <div className="w-full md:w-80 relative">
            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              placeholder="Tìm mã đơn, tên công dân, cán bộ, lãnh đạo..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
              className="w-full pl-9 pr-3 py-2 text-xs rounded-lg border border-gray-200 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="w-full md:w-auto flex flex-wrap items-center gap-2">
            {/* Filter by Reception Type */}
            <div className="w-full sm:w-56">
              <CustomSelect
                value={sourceType}
                onChange={(val) => {
                  const v = typeof val === 'object' ? val?.target?.value : val;
                  setSourceType(v);
                  setCurrentPage(1);
                }}
                options={[
                  { value: "ALL", label: `Tất cả loại đánh giá (${counterStats.totalRatings + leaderStats.totalRatings + phanAnhStats.totalRatings})` },
                  { value: "COUNTER", label: `Quầy tiếp dân (${counterStats.totalRatings})` },
                  { value: "LEADER", label: `Gặp lãnh đạo (${leaderStats.totalRatings})` },
                  { value: "PHAN_ANH", label: `Phản ánh kiến nghị (${phanAnhStats.totalRatings})` }
                ]}
              />
            </div>

            {/* Filter by Star */}
            <div className="w-full sm:w-48">
              <CustomSelect
                value={scoreFilter}
                onChange={(val) => {
                  const v = typeof val === 'object' ? val?.target?.value : val;
                  setScoreFilter(v);
                  setCurrentPage(1);
                }}
                placeholder="Tất cả mức sao"
                options={[
                  { value: "", label: "Tất cả mức sao" },
                  { value: "5", label: "5 Sao ⭐ (Rất hài lòng)" },
                  { value: "4", label: "4 Sao ⭐ (Hài lòng)" },
                  { value: "3", label: "3 Sao ⭐ (Bình thường)" },
                  { value: "2", label: "2 Sao ⭐ (Chưa hài lòng)" },
                  { value: "1", label: "1 Sao ⭐ (Rất chưa hài lòng)" }
                ]}
              />
            </div>

            {/* Reset Filter Button */}
            {(search || scoreFilter || sourceType !== 'ALL') && (
              <button
                type="button"
                onClick={() => {
                  setSearch('');
                  setScoreFilter('');
                  setSourceType('ALL');
                  setCurrentPage(1);
                }}
                className="p-2 text-gray-500 hover:text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 text-xs flex items-center gap-1 cursor-pointer"
                title="Xóa bộ lọc"
              >
                <RotateCcw size={14} />
                <span className="hidden sm:inline">Đặt lại</span>
              </button>
            )}

            {/* Nút Xuất Excel danh sách đánh giá */}
            <button
              type="button"
              onClick={handleExportExcel}
              disabled={exportLoading}
              className="px-3.5 py-2 text-xs font-semibold text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 rounded-lg transition-colors flex items-center gap-1.5 shadow-xs cursor-pointer active:scale-95 ml-auto sm:ml-0"
              title="Xuất file Excel danh sách đánh giá"
            >
              {exportLoading ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <Download className="w-3.5 h-3.5" />
              )}
              <span>{exportLoading ? "Đang xuất..." : "Xuất Excel"}</span>
            </button>
          </div>
        </div>

        {/* Table Content */}
        <div className="w-full overflow-x-auto rounded-b-xl">
          <table className="w-full min-w-[960px] text-left text-xs border-collapse">
            <thead>
              <tr className="bg-gray-50 text-gray-600 font-semibold border-b border-gray-200">
                <th className="pl-6 pr-3 py-3.5 whitespace-nowrap" style={{ width: '13%' }}>
                  Mã phiếu / Đơn
                </th>
                <th className="px-3 py-3.5 whitespace-nowrap" style={{ width: '13%' }}>
                  Loại tiếp nhận
                </th>
                <th className="px-3 py-3.5" style={{ width: '15%' }}>
                  Người dân
                </th>
                <th className="px-3 py-3.5" style={{ width: '19%' }}>
                  Cán bộ / Lãnh đạo tiếp
                </th>
                <th className="px-3 py-3.5" style={{ width: '20%' }}>
                  Mức đánh giá
                </th>
                <th className="px-3 py-3.5 whitespace-nowrap" style={{ width: '11%' }}>
                  Thời gian
                </th>
                <th className="px-2 py-3.5 text-center whitespace-nowrap" style={{ width: '9%' }}>
                  Thao tác
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {loading ? (
                <tr>
                  <td colSpan={7} className="py-12 text-center text-gray-400">
                    <Loader2 className="w-6 h-6 animate-spin text-blue-600 mx-auto mb-2" />
                    Đang tải dữ liệu đánh giá...
                  </td>
                </tr>
              ) : paginatedRatings.length === 0 ? (
                <tr>
                  <td colSpan={7} className="py-12 text-center text-gray-400">
                    <Star className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                    Không có phiếu đánh giá nào phù hợp với bộ lọc.
                  </td>
                </tr>
              ) : (
                paginatedRatings.map((item, idx) => (
                  <tr key={`${item.sourceType}-${item.id || idx}`} className="hover:bg-gray-50/80 transition-colors">
                    {/* Mã phiếu */}
                    <td className="pl-6 pr-3 py-3.5 font-bold text-blue-600 whitespace-nowrap overflow-hidden">
                      <button
                        type="button"
                        onClick={() => setSelectedRating(item)}
                        className="hover:underline hover:text-blue-800 text-left font-bold text-blue-600 cursor-pointer transition-colors truncate block max-w-full"
                        title={item.receptionCode}
                      >
                        {item.receptionCode}
                      </button>
                    </td>

                    {/* Loại tiếp nhận */}
                    <td className="px-3 py-3.5 whitespace-nowrap overflow-hidden">
                      {item.sourceType === 'COUNTER' ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold bg-blue-50 text-blue-700 border border-blue-200">
                          <Building2 size={12} /> Quầy tiếp dân
                        </span>
                      ) : item.sourceType === 'LEADER' ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold bg-purple-50 text-purple-700 border border-purple-200">
                          <UserCheck size={12} /> Gặp lãnh đạo
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold bg-amber-50 text-amber-700 border border-amber-200">
                          <MessageSquare size={12} /> Phản ánh
                        </span>
                      )}
                    </td>

                    {/* Công dân */}
                    <td className="px-3 py-3.5 overflow-hidden">
                      <div className="font-semibold text-gray-900 text-[13px] truncate" title={item.citizenName}>
                        {item.citizenName}
                      </div>
                    </td>

                    {/* Cán bộ / Lãnh đạo tiếp */}
                    <td className="px-3 py-3.5 overflow-hidden">
                      <div className="font-semibold text-gray-900 text-xs truncate" title={item.targetName}>
                        {item.targetName}
                      </div>
                      <div className="text-gray-500 text-[11px] mt-0.5 truncate" title={item.placeLabel}>
                        {item.placeLabel}
                      </div>
                    </td>

                    {/* Mức đánh giá & Nhận xét */}
                    <td className="px-3 py-3.5 overflow-hidden">
                      <div className="flex items-center gap-1.5">
                        {renderStars(item.score)}
                        <span className={`px-1.5 py-0.2 rounded text-[11px] font-bold border ${scoreBg(item.score)}`}>
                          {item.score}★
                        </span>
                      </div>
                      {item.comment && (
                        <p className="text-gray-600 text-xs mt-1 line-clamp-2 break-words italic leading-relaxed" title={item.comment}>
                          "{item.comment}"
                        </p>
                      )}
                    </td>

                    {/* Thời gian */}
                    <td className="px-3 py-3.5 text-gray-600 whitespace-nowrap text-xs">
                      <div className="font-medium">
                        {item.ratedAt ? new Date(item.ratedAt).toLocaleDateString('vi-VN') : '---'}
                      </div>
                      <div className="text-[10px] text-gray-400">
                        {item.ratedAt ? new Date(item.ratedAt).toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' }) : ''}
                      </div>
                    </td>

                    {/* Thao tác */}
                    <td className="px-2 py-3.5 text-center whitespace-nowrap">
                      <button
                        type="button"
                        className="px-2.5 py-1.5 rounded-lg border border-gray-200 text-gray-700 bg-white hover:bg-gray-50 hover:text-blue-600 hover:border-blue-300 transition-colors inline-flex items-center gap-1 text-xs font-medium shadow-2xs cursor-pointer"
                        onClick={() => setSelectedRating(item)}
                        title="Xem chi tiết phiếu đánh giá"
                      >
                        <Eye size={13} className="text-blue-600" />
                        <span>Chi tiết</span>
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* PAGINATION */}
        {totalPages > 1 && (
          <div className="p-3 border-t border-gray-100 flex items-center justify-between bg-gray-50/50 text-xs text-gray-500">
            <span>
              Trang {currentPage} / {totalPages} ({totalItems} kết quả)
            </span>
            <div className="flex items-center gap-1">
              <button
                type="button"
                disabled={currentPage <= 1}
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                className="p-1.5 rounded border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-40 cursor-pointer"
              >
                <ChevronLeft size={16} />
              </button>
              <button
                type="button"
                disabled={currentPage >= totalPages}
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                className="p-1.5 rounded border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-40 cursor-pointer"
              >
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* DETAIL MODAL */}
      <Modal
        title={
          <div className="font-bold text-gray-900 text-base flex items-center gap-2">
            <span>Chi tiết phiếu đánh giá</span>
            {selectedRating?.sourceType === 'COUNTER' ? (
              <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-blue-50 text-blue-700 border border-blue-200">Tiếp dân tại quầy</span>
            ) : selectedRating?.sourceType === 'LEADER' ? (
              <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-purple-50 text-purple-700 border border-purple-200">Gặp lãnh đạo</span>
            ) : (
              <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-amber-50 text-amber-700 border border-amber-200">Phản ánh kiến nghị</span>
            )}
          </div>
        }
        open={Boolean(selectedRating)}
        onCancel={() => setSelectedRating(null)}
        footer={[
          <button
            key="close"
            onClick={() => setSelectedRating(null)}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors cursor-pointer"
          >
            Đóng
          </button>
        ]}
        width={650}
      >
        {selectedRating && (
          <div className="space-y-3 pt-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-gray-50 p-3 rounded-lg">
                <span className="text-xs text-gray-500 block">
                  {selectedRating.sourceType === 'PHAN_ANH' ? 'Mã phản ánh' : 'Mã tiếp nhận'}
                </span>
                <span className="font-bold text-blue-600 text-sm">{selectedRating.receptionCode}</span>
              </div>
              <div className="bg-gray-50 p-3 rounded-lg">
                <span className="text-xs text-gray-500 block">Mức đánh giá</span>
                <div className="flex items-center gap-1.5 mt-0.5">
                  {renderStars(selectedRating.score)}
                  <span className={`px-2 py-0.5 rounded text-xs font-bold border ${scoreBg(selectedRating.score)}`}>
                    {selectedRating.score} / 5 ⭐
                  </span>
                </div>
              </div>
              <div className="bg-gray-50 p-3 rounded-lg">
                <span className="text-xs text-gray-500 block">
                  {selectedRating.sourceType === 'PHAN_ANH' ? 'Người gửi phản ánh' : 'Họ tên công dân'}
                </span>
                <span className="font-bold text-gray-900 text-sm">{selectedRating.citizenName}</span>
              </div>
              <div className="bg-gray-50 p-3 rounded-lg">
                <span className="text-xs text-gray-500 block">
                  {selectedRating.sourceType === 'COUNTER'
                    ? 'Cán bộ tiếp nhận'
                    : selectedRating.sourceType === 'LEADER'
                    ? 'Lãnh đạo chủ trì'
                    : 'Lĩnh vực phản ánh'}
                </span>
                <span className="font-bold text-blue-700 text-sm">{selectedRating.targetName}</span>
              </div>
              <div className="bg-gray-50 p-3 rounded-lg sm:col-span-2">
                <span className="text-xs text-gray-500 block">
                  {selectedRating.sourceType === 'PHAN_ANH' ? 'Tiêu đề phản ánh' : 'Địa điểm / Quầy'}
                </span>
                <span className="font-medium text-gray-800 text-sm">{selectedRating.placeLabel}</span>
              </div>
            </div>

            {(selectedRating.workingContent || selectedRating.topic) && (
              <div className="bg-gray-50 p-3 rounded-lg">
                <span className="text-xs text-gray-500 block mb-1 font-semibold">
                  {selectedRating.sourceType === 'PHAN_ANH' ? 'Chi tiết phản ánh' : 'Nội dung / Lý do buổi làm việc'}
                </span>
                <p className="text-xs text-gray-700 whitespace-pre-wrap">
                  {selectedRating.workingContent || selectedRating.topic}
                </p>
              </div>
            )}

            {/* Gợi ý tiêu chí nếu có */}
            {Array.isArray(selectedRating.selectedSuggestions || selectedRating.ly_do) && (selectedRating.selectedSuggestions || selectedRating.ly_do).length > 0 && (
              <div className="bg-gray-50 p-3 rounded-lg">
                <span className="text-xs text-gray-500 block mb-1.5 font-semibold">Tiêu chí hài lòng</span>
                <div className="flex flex-wrap gap-1.5">
                  {(selectedRating.selectedSuggestions || selectedRating.ly_do).map((reason, idx) => (
                    <span key={idx} className="inline-flex items-center gap-1 px-2.5 py-1 bg-white border border-emerald-200 text-emerald-700 rounded-md text-xs font-medium">
                      <CheckCircle2 size={12} className="text-emerald-500" />
                      {reason}
                    </span>
                  ))}
                </div>
              </div>
            )}

            <div className="bg-gray-50 p-3 rounded-lg">
              <span className="text-xs text-gray-500 block mb-1 font-semibold">Nhận xét của công dân</span>
              <p className="text-xs text-gray-800 italic">
                {selectedRating.comment ? `"${selectedRating.comment}"` : 'Không có nhận xét thêm.'}
              </p>
            </div>

            <div className="bg-gray-50 p-3 rounded-lg">
              <span className="text-xs text-gray-500 block mb-1">Thời gian gửi đánh giá</span>
              <span className="text-xs text-gray-800 font-medium">
                {selectedRating.ratedAt ? new Date(selectedRating.ratedAt).toLocaleString('vi-VN') : '---'}
              </span>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
